import { toast } from "@suraasa/placebo-ui"
import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query"
import api from "api"
import { queries } from "api/queries"
import {
  ToolType,
  VideoSummaryForm,
  VideoSummaryGenerateResponse,
  VideoSummaryPromptData,
} from "api/resources/aiTools/types"
import { APIResponse } from "api/types"
import { APIError } from "api/utils"
import { getToolName } from "features/AItools/helper"
import {
  StreamFetchProps,
  StreamQuery,
  StreamResponseEnum,
  useStreamQuery,
} from "features/AItools/hooks/useStreamQuery"
import { useEffect, useState } from "react"
import { flushSync } from "react-dom"
import { UseFormSetError } from "react-hook-form"
import { useNavigate, useSearchParams } from "react-router-dom"
import routes from "routes"
import { handleErrors } from "utils/helpers"
import { trackingService } from "utils/tracking"
import { TrackingEvents } from "utils/tracking/events"

export type VideoSummaryReturnType = {
  currentResponseId: number | null
  setCurrentResponseId: (id: number | null) => void
  promptDataId: string | number | null
  setPromptDataId: React.Dispatch<React.SetStateAction<string | number | null>>
  responseIds: number[] | null
  setResponseIds: React.Dispatch<React.SetStateAction<number[] | null>>
  overviewData: VideoSummaryPromptData | undefined

  userVote: UseMutationResult<
    never,
    unknown,
    {
      reaction: boolean | undefined | null
    },
    unknown
  >

  output: string | undefined
  isLoading: boolean
  stream: Omit<StreamQuery, "refetchAsync"> & {
    refetchAsync: <T>(
      props: Omit<StreamFetchProps<T>, "toolType">
    ) => Promise<void>
  }

  regenerateStream: StreamQuery
  generateTool: UseMutationResult<
    APIResponse<VideoSummaryGenerateResponse>,
    any,
    {
      data: VideoSummaryForm
      setError: UseFormSetError<VideoSummaryForm>
    },
    unknown
  >

  regenerateResponse: UseMutationResult<
    APIResponse<{
      id: number
    }>,
    unknown,
    {
      reason: string
    },
    unknown
  >
  isPositiveResponse: boolean | null | undefined
  pageLoading: boolean
  resetState: () => void
  onBack: () => void
}

export const useVideoSummary = (): VideoSummaryReturnType => {
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const id = searchParams.get("id")
  const navigate = useNavigate()
  const [overviewData, setOverviewData] = useState<
    VideoSummaryPromptData | undefined
  >()
  const [isLoading, setIsLoading] = useState(false)
  const [isPositiveResponse, setIsPositiveResponse] = useState<
    boolean | null | undefined
  >()
  const [currentResponseId, setCurrentResponseId] = useState<number | null>(
    null
  )
  const [promptDataId, setPromptDataId] = useState<string | number | null>(id)
  const [responseIds, setResponseIds] = useState<number[] | null>(null)

  const [output, setOutput] = useState<string>("")
  const [pageLoading, setPageLoading] = useState(false)

  const getContent = async (id: number) => {
    // resetStream()
    setOutput("")
    setIsLoading(true)
    const res = await api.aiTools.videoSummary.retrieveContent({
      urlParams: {
        id: id,
      },
    })
    if (res instanceof APIError) {
      toast.error(res.message || "Something went wrong")
      setIsLoading(false)
      return
    }

    setIsPositiveResponse(res.isPositiveResponse)
    setOutput(res.output)
    setIsLoading(false)
    // resetStream()
  }

  const resetState = () => {
    setOutput("")
    setPromptDataId(null)
    setCurrentResponseId(null)
    setIsPositiveResponse(undefined)
    setOverviewData(undefined)
    setResponseIds(null)
  }
  const onBack = () => {
    navigate(routes.aiTools.videoSummary, { replace: true })
    resetState()
  }
  // This flow is for when user clicks on history item
  // If that is the case then we just fetch the overview and the latest generated output
  useEffect(() => {
    if (id) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPageLoading(true)
      api.aiTools.videoSummary
        .getPromptData({
          urlParams: {
            id: id!,
          },
        })
        .then(async data => {
          setPromptDataId(id)
          const { responseIds } = data

          if (responseIds) {
            const [latestResponseId] = responseIds
            setCurrentResponseId(latestResponseId)
            setResponseIds(responseIds)
            await getContent(latestResponseId)
          }
          setOverviewData(data)
          setPageLoading(false)
        })
        .catch(error => {
          console.error("Error fetching prompt data:", error)
          setPageLoading(false)
        })
    }
  }, [id])

  const overviewAPI = async (id: string | number | null) => {
    const res = await api.aiTools.videoSummary.getPromptData({
      urlParams: {
        id: id!,
      },
    })
    if (res instanceof APIError) {
      toast.error("Prompt failed to generate")
      return
    }

    const { responseIds } = res

    if (responseIds) {
      const [latestResponseId] = responseIds
      setCurrentResponseId(latestResponseId)
      setResponseIds(responseIds)
    }

    setOverviewData(res)
  }

  const generateTool = useMutation({
    mutationFn: async ({
      data,
    }: {
      data: VideoSummaryForm
      setError: UseFormSetError<VideoSummaryForm>
    }) => {
      return api.aiTools.videoSummary.generate({ data })
    },
    onSuccess: async res => {
      const { responseId, id } = res
      resetState()
      flushSync(() => {
        setCurrentResponseId(responseId)
        setPromptDataId(id)
        setOverviewData({
          id: res.id,
          url: res.url,
          language: res.language,
          startTime: res.startTime,
          endTime: res.endTime,
          title: `New ${getToolName(ToolType.videoSummary)}`,
        })
      })

      await streamOutput.refetchAsync({
        currentResponseId: responseId,
      })
      await streamTitle.refetchAsync({
        currentResponseId: responseId,
      })
      try {
        await overviewAPI(id)
      } catch (error) {
        // Ignore errors from overviewAPI as the UI already has the necessary data
        // from the generate response. The prompt data may not be immediately available
        // in the backend, causing a race condition.
        console.error("Error fetching overview data:", error)
      }
      queryClient.refetchQueries({
        queryKey: queries.aiTools.listHistory(ToolType.videoSummary).queryKey,
        type: "active",
        exact: true,
      })
    },
    onError: (err: any, { setError }) => {
      if (err instanceof APIError) {
        if ((err?.statusCode || 0) >= 500) {
          toast.error("We're unable to process your request", {
            description: "Please try again later",
          })
          return
        }
        if (err.errors.message) {
          toast.error(err.errors.message)
          return
        }
        if (err.errors.fieldErrors) {
          handleErrors(err, { setter: setError })
          return
        }
      }
      toast.error("We're unable to process your request", {
        description: "Please try again later",
      })
    },
  })

  const streamTitle = useStreamQuery({
    responseType: StreamResponseEnum.title,
    toolType: ToolType.videoSummary,
  })

  const streamOutput = useStreamQuery({
    responseType: StreamResponseEnum.generate,
    toolType: ToolType.videoSummary,
    onError: () => {
      toast.error("Generation Failed")
    },
    onStreaming(chunks) {
      setOutput(prev => (prev += chunks))
    },
  })

  const regenerateStream = useStreamQuery({
    responseType: StreamResponseEnum.regenerate,
    toolType: ToolType.videoSummary,
    onSuccess: () => {
      toast.success("Re-generated Successfully")
    },
    onError: () => {
      toast.error("Re-generation Failed")
    },
    onStreaming(chunks) {
      setOutput(prev => (prev += chunks))
    },
  })

  const userVote = useMutation({
    mutationFn: async ({
      reaction,
    }: {
      reaction: boolean | undefined | null
    }) => {
      const eventName = reaction
        ? TrackingEvents.aiToolOutputUpvoted
        : TrackingEvents.aiToolOutputDownvoted
      const toolType = ToolType.videoSummary

      trackingService.trackEvent(eventName, {
        tool_type: toolType,
      })

      return api.aiTools.videoSummary.updateData({
        urlParams: { id: currentResponseId!, type: toolType },
        data: {
          isPositiveResponse: reaction,
        },
      })
    },
  })

  const regenerateResponse = useMutation({
    mutationFn: ({ reason }: { reason: string }) =>
      api.aiTools.videoSummary.regenerateResponse({
        urlParams: {
          id: currentResponseId!,
        },
        data: {
          regenerateInstruction: reason,
        },
      }),
    onSuccess: async (res, { reason }) => {
      flushSync(() => {
        setCurrentResponseId(res.id)
        setOutput("")
      })

      await regenerateStream.refetchAsync({
        currentResponseId: res.id,
        regenReason: reason,
      })
      try {
        await overviewAPI(id || promptDataId)
      } catch (error) {
        console.error("Error fetching overview data:", error)
      }
    },
    onError: err => {
      if (err instanceof APIError) {
        if ((err?.statusCode || 0) >= 500) {
          toast.error("We're unable to process your request", {
            description: "Please try again later",
          })
          return
        }
        if (err.errors.message) {
          toast.error(err.errors.message)
          return
        }
      } else {
        toast.error("We're unable to process your request", {
          description: "Please try again later",
        })
      }
    },
  })

  return {
    promptDataId,
    generateTool,
    setPromptDataId,
    currentResponseId,
    setCurrentResponseId: id => {
      setCurrentResponseId(id)
      if (id) {
        getContent(id)
      }
    },
    responseIds,
    setResponseIds,
    stream: {
      ...streamOutput,
      refetchAsync: ({ currentResponseId: id }) =>
        streamOutput.refetchAsync({
          currentResponseId: id,
        }),
    },

    regenerateResponse,
    overviewData,
    userVote,
    regenerateStream,

    output,
    isLoading:
      isLoading || regenerateStream.isLoading || streamOutput.isLoading,
    isPositiveResponse,
    resetState,
    onBack,
    pageLoading,
  }
}
